/*
###################################################################
Fitinsur Open Insurance - Copyright 2019-2023 - All Rights Reserved
###################################################################
*/
/* eslint-disable no-unused-expressions */

import React, { useContext, useEffect, useRef, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { Field, FieldArray, Form, Formik } from "formik";
import Colors from "constants/Colors";
import { GlobalAuthContext, GlobalLoaderContext } from "store/context";
import { Layout, Text } from "components";
import { Col, Row } from "components/Grid";
import Icon from "components/Icon";
import Loader from "components/Loader";
import {
  GridColumnTitle,
  InputField,
  Label,
  SelectField,
} from "components/Form";
import { FitinsurAPI, MessageCenter } from "utils";
import {
  FieldGrid,
  ColIcon,
  ButtonContainer,
  Content,
  LoadContainer,
  MobileLabel,
  RowFilterOrder,
  RowFilterIcon,
  FilterSearchContainer,
  RowFilter,
  FilterColumn,
  CheckBoxColumn,
  FilterLabel,
  DialogCancel,
  DialogConfirm,
  TooltipCard,
  TooltipText,
  TooltipBox,
} from "./styles";
import { Button } from "components/index";
import { FilterContainer } from "../list2/styles";
import Checkbox from "components/Form/CheckBox";
import MultiSelectField from "components/Form/MultiSelectField";
import DialogComponent from "components/Dialog/index";
import CacheManager from "utils/src/cache-manager";
import useWindowScroll from "services/useWindowScroll";
import { ResponsiveTable } from '@fitinsur/components';
import ListEnquiryButtons from 'components/ListEnquiryButtons';
import NotesModalV2 from "components/NotesModalV2";
import Access from 'constants/Access';

export default function ClaimPage(props) {
  let scrollUpdate = useWindowScroll();
  const cache = new CacheManager();
  const loading = useContext(GlobalLoaderContext);
  const authContext = useContext(GlobalAuthContext);
  const fitinsur = new FitinsurAPI();
  const mc = new MessageCenter();
  const history = useHistory();
  const location = useLocation();
  const formik = useRef();
  const [headerStatus, setHeaderStatus] = useState([]);
  const [sortBy, setSortBy] = useState({
    key: null,
    direction: null,
  });
  const [currentOperation, setCurrentOperation] = useState(null);
  const [products, setProducts] = useState(
    [
      {
        label: "NO PRODUCTS",
        value: ""
      }
    ]
  );

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [claims, setClaims] = useState([]);
  const [filtersList, setFiltersList] = useState([]);
  const [filterOptions, setFilterOptions] = useState([]);
  const [isAbleEdit, setIsAbleEdit] = useState(false);
  const [isAbleDelete, setIsAbleDelete] = useState(false);
  const [isAbleCreate, setIsAbleCreate] = useState(false);
  const [typeDialog, setTypeDialog] = useState(null);
  const [tooltips, setTooltips] = useState([]);
  const [dialog, setDialog] = useState({
    visible: false,
    title: "",
    icon: "times-circle",
    args: null,
  });

  const initialValues = {
    filter: {
      name: "",
      referenceNumber: "",
      rootOperationReferenceNumber: "",
      claimUcr: "",
      claimEcfTransaction: "",
      client: "",
      mainParty: "",
      status: "",
    },
  };
  const [filterSelected, setFilterSelected] = useState(initialValues);
  const region = authContext.region === 'en-GB' ? 'en' : 'en';

  const handleColorLabel = (item) => {
    if (item.isRejected) return Colors.GRAY[3];
    else if (item.requireAttention) return Colors.RED;
    else if (item.isApproved) return Colors.GRAY[0];
    else if (item.mediationFinished) return Colors.PRIMARY1;
    else return Colors.GRAY[0];
  }

  const columns = [
    {
      title: "REFERENCE",
      key: "rootOperationReferenceNumber",
      sortKey: "rootOperationReferenceNumber",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item)
    },
    {
      title: "CLAIM ID",
      key: "referenceNumber",
      sortKey: "referenceNumber",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item)
    },
    {
      title: "CLIENT",
      key: "nmClient",
      sortKey: "nmClient",
      minWidth: 18,
      maxWidth: 18,
      color: (item) => handleColorLabel(item)
    },
    {
      title: "MAIN PARTY",
      key: "projectName|mainParty",
      sortKey: "projectName|mainParty",
      minWidth: 18,
      maxWidth: 18,
      color: (item) => handleColorLabel(item)
    },
    {
      title: "UCR",
      key: "claimUcr",
      sortKey: "claimUcr",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item)
    },
    {
      title: "ECF",
      key: "claimEcfTransaction",
      sortKey: "claimEcfTransaction",
      minWidth: 14,
      maxWidth: 14,
      color: (item) => handleColorLabel(item)
    },
    {
      title: "STATUS",
      key: "status",
      minWidth: 18,
      maxWidth: 18,
      color: (item) => handleColorLabel(item)
    },
    {
      title: "ACTIONS",
      key: "actions",
      minWidth: 10,
      maxWidth: 10,
      color: (item) => handleColorLabel(item),
      paddingRight: 0,
      component: (listItem) => <ListEnquiryButtons
        width={60}
        editRoute={checkOperationRoute}
        listItem={listItem}
        idOperation={listItem.idOperation}
        editPermission={true}
        setCurrentOperation={setCurrentOperation}
        authContextToken={authContext?.token}
        iconTooltips={tooltips}
        toDocument={() => history.push(`/broking/document-management/${listItem.idOperation}`)}
        icons={['edit', 'folder']}
      />
    },
  ];

  const deleteFilter = () => {
    if (filterSelected.filterName) {
      setTypeDialog('deleteFilter');
      setDialog({
        visible: true,
        title: 'REMOVE FILTER',
        icon: 'exclamation',
        args: null
      })
    }
  }

  function resetFilters() {
    requestCustomFilterList('reset');
    formik.current.resetForm(initialValues);
    setCurrentPage(1);
    setSortBy({ key: null, direction: null });
    handleFilter(initialValues.filter);
  }

  function handleSortList(key) {
    let direction = "asc";
    if (sortBy.key === key && sortBy.direction === "asc") direction = "desc";
    const sort = { key: key, direction: direction };

    setSortBy(sort);
    setCurrentPage(1);
    getClaims(1, sort, `${location.search}`);
  }

  const jsonToQueryString = (json) => {
    const keys = Object.keys(json).filter((key) => json[key]);
    return keys
      .map((key, index) => {
        const sufix = index + 1 === keys.length ? "" : "&";
        return (
          encodeURIComponent(key) + "=" + encodeURIComponent(json[key]) + sufix
        );
      })
      .join("");
  };

  const handleFilter = (filters) => {
    const formikFilters = formik.current.values.filter;
    const { ...rest } = formikFilters;

    const filterObject = { ...rest, ...filters };
    const queryString = jsonToQueryString(filterObject);
    const filterQueryString = `?${queryString}`;

    setCurrentPage(1);
    getClaims(1, {}, filterQueryString);

    if (queryString) {
      localStorage.setItem("claims_filter", filterQueryString);
    } else {
      localStorage.removeItem("claims_filter");
    }

    history.push(`claims${filterQueryString}`);
  };

  const handleSetFilterFromParams = () => {
    if (location.search) {
      const query = new URLSearchParams(location.search);

      if (query.get("referenceNumber")) {
        formik.current?.setFieldValue(
          "filter.referenceNumber",
          query.get("referenceNumber")
        );
      }

      if (query.get("rootOperationReferenceNumber")) {
        formik.current?.setFieldValue(
          "filter.rootOperationReferenceNumber",
          query.get("rootOperationReferenceNumber")
        );
      }

      if (query.get("claimUcr")) {
        formik.current?.setFieldValue("filter.claimUcr", query.get("claimUcr"));
      }

      if (query.get("claimEcfTransaction")) {
        formik.current?.setFieldValue(
          "filter.claimEcfTransaction",
          query.get("claimEcfTransaction")
        );
      }

      if (query.get("client")) {
        formik.current?.setFieldValue("filter.client", query.get("client"));
      }

      if (query.get("mainParty")) {
        formik.current?.setFieldValue(
          "filter.mainParty",
          query.get("mainParty")
        );
      }

      if (query.get("status")) {
        let statusIds = query
          .get('status')
          .split(',')
          .map(item => Number(item))

        formik.current?.setFieldValue("filter.status", statusIds);
      }
      if (query.get("products")) {
        let productsIds = query
          .get('products')
          .split(',')
          .map(item => Number(item))

        formik.current?.setFieldValue("filter.products", productsIds);
      }
    }
  };

  const [showFilter, setShowFilter] = useState(false);

  const renderNewRegisterButton = () => {
    return (
      <Text
        primary
        type="button"
        id="btnSubmit"
        style={{ cursor: "pointer" }}
        onClick={() => history.push("create-claim")}
      >
        + NEW REGISTER
      </Text>
    );
  };

  async function getClaims(
    currentPage = 1,
    sort = {},
    filterQueryString = "?"
  ) {
    loading.start();
    try {
      const url = `${filterQueryString || "?"}&sortBy=${JSON.stringify(
        sort
      )}&currentPage=${currentPage}`;
      fitinsur.setAuthToken(authContext.token);
      const response = await fitinsur
        .requestGetClaims(url)
        .then((response) => response.json());

      if (response.ret_code !== 200) {
        mc.error(response.ret_msg_details);
        loading.stop();
        return;
      }

      const statuses = response.statuses.map((x) => ({
        label: x.description,
        value: x.id,
      }));
      setHeaderStatus(statuses);

      setClaims(response.ret_value);

      const pagination = { ...response.pagination_control };

      if (+pagination.currentPage === 1) {
        setClaims(response.ret_value)
      } else {
        setClaims([...claims, ...response.ret_value]);
      }

      pagination.currentPage = parseInt(pagination.currentPage);
      setTotalPages(response.pagination_control.totalPages);
      loading.stop();
    } catch (err) {
      loading.stop();
    }
  }

  async function requestProductsList() {
    const response = await fitinsur
      .requestAccess("", "CREATE", Access.CREATE_FRAMEWORK, null, null, authContext.token)
      .then((response) => response.json())

    const hasPermission = response && response.ret_code === 200;

    fitinsur.requestProductsList()
      .then(x => x.json())
      .then(response => {
        if (response.ret_code !== 200) {
          return;
        }

        const options = response.ret_value
          .map(product => ({
            label: product.PRODUCT_NAME,
            value: product.ID_PRODUCT
          }))
          .filter(product => {
            if (product.value === 16 && !hasPermission) {
              return;
            } else {
              return product;
            }
          })
        setProducts(options);
      })
      .catch(error => console.log(error));

  }

  function setNextPage() {
    if (currentPage === totalPages) return;
    setCurrentPage(currentPage + 1);
    getClaims(currentPage + 1, sortBy, `${location.search}`);
  }

  const handleGetPreviusFilter = () => {
    const filterQueryString = localStorage.getItem("claims_filter");

    if (filterQueryString) {
      history.push(`claims${filterQueryString}`);
      getClaims(1, sortBy, filterQueryString);
    } else {
      getClaims();
    }
  };


  const checkOperationRoute = (idOperation) => {
    fitinsur.setAuthToken(authContext.token);
    loading.start();

    fitinsur.requestPolicyNavigation(idOperation).then(x => x.json()).then(res => {
      loading.stop();

      if (res.ret_code != 200) {
        mc.error(res.ret_msg_details);
        return;
      }

      let { route } = res.ret_value;
      console.log('RESPONSE NAVIGATION', res.ret_value);

      if (route) {
        history.push({ pathname: route })
      } else {
        mc.error('NAVIGATION NOT DEFINED');
        loading.stop();
      }
    }).catch(() => loading.stop());
  }

  const handleExportClaims = async (page = 1, sort = {}, filterQueryString = '?') => {
    let exportList = sessionStorage.getItem('texel-export-list-report');
    let list = [];
    if (exportList) list = JSON.parse(sessionStorage.getItem('texel-export-list-report'))
    let verify = list.length > 0 ? list.filter(item => item.nameList == 'Claims Report') : false
    if (!verify) {
      loading.start();
      setTimeout(() => loading.stop(), 2000);

      const url = `${filterQueryString}&sortBy=${JSON.stringify(sort)}&currentPage=${page}`;

      fitinsur.setAuthToken(authContext.token);
      const document = await fitinsur.exportClaims(url).then(x => x.json());
      loading.start();
      setTimeout(() => loading.stop(), 2000);
      if (document.idDocument) {
        list.push({ idDocument: document.idDocument, processing: true, nameList: 'Claims Report' });
        sessionStorage.setItem('texel-export-list-report', JSON.stringify(list));
      }
    } else {
      mc.error("Please, wait claims report be downloaded.");
    }
  }

  const editCustomFilter = (filter) => {
    if (filter.filter.name.length <= 0) return mc.error("Filter name must be provided");
    if (!filter.filter.name) {
      mc.error("Please provide a name");
      return;
    }
    const obj = {
      filterName: filter.filter.name,
      id: filter.filter.id,
      idDomain: 320,
      idDomainApplication: 388,
      filterArgs: {
        referenceNumber: filter.filter.referenceNumber,
        rootOperationReferenceNumber: filter.filter.rootOperationReferenceNumber,
        claimUcr: filter.filter.claimUcr,
        claimEcfTransaction: filter.filter.claimEcfTransaction,
        client: filter.filter.client,
        mainParty: filter.filter.mainParty,
        status: filter.filter.status,
        products: filter.filter.products,
      }
    }
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestUpdateCustomFilter(obj)
      .then(x => x.json())
      .then(res => {
        if (res.ret_code !== 200) {
          mc.error("An error happened, please try again.");
          return;
        }
        mc.success("Filter updated successfully");
        setIsAbleEdit(!isAbleEdit)
        setFilterSelected(filtersList[0])
        requestCustomFilterList('edit')
      })
  }

  const createCustomFilter = (filter) => {
    if (!filter.filter.name) {
      mc.error("Please provide a name");
      return;
    }
    const obj = {
      filterName: filter.filter.name,
      idDomain: 320,
      idDomainApplication: 388,
      filterArgs: {
        referenceNumber: filter.filter.referenceNumber,
        rootOperationReferenceNumber: filter.filter.rootOperationReferenceNumber,
        claimUcr: filter.filter.claimUcr,
        claimEcfTransaction: filter.filter.claimEcfTransaction,
        client: filter.filter.client,
        mainParty: filter.filter.mainParty,
        status: filter.filter.status,
        products: filter.filter.products,
      }
    }
    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestCreateCustomFilter(obj)
      .then(x => x.json())
      .then(res => {
        if (res.ret_code !== 200) {
          mc.error("An error happened, please try again.");
          return;
        }
        mc.success("Filter created successfully");
        setIsAbleCreate(!isAbleCreate)
        requestCustomFilterList('create')
      })
  }

  const deleteCustomFilter = (selectedFilter) => {
    if (selectedFilter.filterName !== 'DEFAULT') {
      const obj = {
        filterName: selectedFilter.filterName,
        id: selectedFilter.id,
        idDomain: selectedFilter.idDomain,
        idDomainApplication: selectedFilter.idDomainApplication,
      }
      fitinsur.setAuthToken(authContext.token);
      fitinsur.requestDeleteCustomFilter(obj)
        .then(x => x.json())
        .then(res => {
          if (res.ret_code !== 200) {
            mc.error("An error happened, please try again.");
            return;
          }
          mc.success('successfully deleted filter')
          setIsAbleDelete(false)
          setShowFilter(false)
          requestCustomFilterList('delete')
        })
    } else {
      mc.error("This filter cannot be removed!")
    }
  }

  const requestCustomFilterList = (action) => {

    const obj = {
      idDomain: 320,
      idDomainApplication: 388
    }

    fitinsur.setAuthToken(authContext.token);
    fitinsur.requestGetCustomFilter(obj)
      .then(x => x.json())
      .then(res => {
        if (res.ret_code !== 200) {
          mc.error("An error happened, please try again.");
          return;
        }
        const enquiryFilterList = res.ret_value.filter(
          element => {
            return element.idDomain === 320 && element.idDomainApplication === 388
          }
        )
        const option = []
        enquiryFilterList.map((element, index) => {
          let opt = {
            label: element.filterName,
            value: index
          }
          option.push(opt)
        })
        setFilterOptions(option)
        setFiltersList(enquiryFilterList)

        const query = new URLSearchParams(location.search);
        let name = query.get('name')
        let index = option.find(option => option.label === name)?.value
        if (action === 'search' || action === 'load' || name) {
          formik.current?.setFieldValue("filtersList", index);
          const value = {
            filterName: name,
            referenceNumber: "",
            rootOperationReferenceNumber: "",
            claimUcr: "",
            claimEcfTransaction: "",
            client: "",
            mainParty: "",
            status: "",
          };
          setFilterSelected(value)
        } if (action === 'create') {
          const filterCreated = option[option.length - 1]
          formik.current?.setFieldValue("filtersList", filterCreated.value);
          const value = {
            filterName: filterCreated.label,
            referenceNumber: "",
            rootOperationReferenceNumber: "",
            claimUcr: "",
            claimEcfTransaction: "",
            client: "",
            mainParty: "",
            status: "",
          };
          setFilterSelected(value)
        } if (action === 'load' && !name || action === 'reset') {
          setFilterSelected(enquiryFilterList[0])
          formik.current?.setFieldValue("filtersList", 0);
        }
      })
  }

  const controlFilter = (action) => {
    if (action === 'edit' && filterSelected.filterName && filterSelected.filterName != 'DEFAULT') {
      setIsAbleEdit(!isAbleEdit)
      setIsAbleDelete(false)
      setIsAbleCreate(false)
    }
    if (action === 'create') {
      setIsAbleEdit(false)
      setIsAbleDelete(false)
      setIsAbleCreate(!isAbleCreate)
      setFilterSelected(initialValues)
    }
    if (action === 'delete') {
      if (filterSelected.filterName === 'DEFAULT') return;
      deleteFilter()
    }
  }

  const handleCustomFilter = (value) => {
    if (isAbleEdit) {
      if (!value.filter.name || value.filter.name === 'DEFAULT' || value.filter.name === 'default') {
        return mc.error("An error happened, incorrect value for filter name.");
      }
      setIsAbleEdit(false)
      return editCustomFilter(value)
    }
    if (isAbleCreate) {
      if (!value.filter.name || value.filter.name === 'DEFAULT' || value.filter.name === 'default') {
        return mc.error("An error happened, incorrect value for filter name.");
      }
      setIsAbleCreate(false)
      return createCustomFilter(value)
    }
    if (!isAbleCreate || !isAbleEdit) {
      setShowFilter(false)
      return handleFilter(value)
    }
  };

  useEffect(() => {
    requestProductsList();
    if (location.search) {
      handleSetFilterFromParams();
      getClaims(1, sortBy, `${location.search}`);
    } else {
      handleGetPreviusFilter();
    }
    requestCustomFilterList('load');

    cache.getTooltip("DOMAINS", region, "357", [585, 586, 581, 588], authContext.token).then(msg => {
      let obj = [];
      msg.map(element => {
        obj[element.MODEL_ITEM_ID] = element.MSG_TEXT;
      })
      setTooltips(obj)
    }).catch(err => console.log("error", err));
  }, []);

  useEffect(() => {
    if (scrollUpdate && !(!claims || claims.length === 0 || currentPage === totalPages)) {
      if (!loading.active) {
        setNextPage()
      }
    }
  }, [scrollUpdate])

  return (
    <Layout title={"Claims"} subtitle={renderNewRegisterButton()}>
      <Content>
        <NotesModalV2
          show={currentOperation}
          handleClose={() => { setCurrentOperation(null) }}
          idOperation={currentOperation}
          label={'Claim notes'}
          onChange={(idOperation, requireAttention) => {
            setClaims(claims.map((claim) => {
              if (idOperation == claim.idOperation) {
                claim.requireAttention = requireAttention;
              }
              return claim;
            }))
          }}
        />
        <DialogComponent

          icon={dialog.icon}
          title={dialog.title}
          show={dialog.visible}
          setDialog={setDialog}
        >
          {typeDialog === 'deleteFilter' &&
            (
              <>
                <DialogCancel onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  setDialog({ visible: false, title: null, icon: null, args: null })
                }}>
                  Cancel
                </DialogCancel>
                <DialogConfirm onClick={(e) => {
                  e.stopPropagation();
                  e.preventDefault();
                  deleteCustomFilter(filterSelected);
                  setFilterSelected(filtersList[0]);
                  setDialog({ visible: false, title: null, icon: null, args: null })
                }}>
                  Confirm
                </DialogConfirm>
              </>
            )
          }

        </DialogComponent>
        <Formik
          initialValues={initialValues}
          onSubmit={(values) => handleFilter(values)}
          innerRef={formik}
        >
          {(formikProps) => {
            return (
              <Form style={{ minHeight: showFilter ? "500px" : "" }}>
                <RowFilterIcon>
                  <FilterContainer
                    className={showFilter ? "filter-open" : "filter-close"}
                  >
                    <Row>
                      <Col
                        style={{ display: "flex", justifyContent: "flex-end" }}
                      >
                        <Icon
                          name="close"
                          className="icon-close"
                          onClick={() => setShowFilter(false)}
                        />
                      </Col>

                    </Row>

                    <RowFilterOrder>
                      <MobileLabel>ORDER</MobileLabel>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          padding: "10px",
                        }}
                      >
                        {showFilter &&
                          columns.map((column, index) => (
                            <Row key={index}>
                              <GridColumnTitle
                                onClick={() =>
                                  column.value && handleSortList(column.value)
                                }
                                style={{
                                  cursor: column.value
                                    ? "pointer"
                                    : "not-allowed",
                                }}
                                className="mb-2 mr-2"
                              >
                                {column.title}
                              </GridColumnTitle>
                              {column.value &&
                                sortBy.key === column.value &&
                                sortBy.direction === "asc" ? (
                                <Icon name={"arrowup"} />
                              ) : column.value &&
                                sortBy.key === column.value &&
                                sortBy.direction === "desc" ? (
                                <Icon name={"arrowdown"} />
                              ) : (
                                ""
                              )}
                            </Row>
                          ))}
                      </div>
                    </RowFilterOrder>
                    <hr className="d-md-none mb-3" />
                    <FilterSearchContainer>
                      <Row>
                        <Col>
                          <MobileLabel>
                            {filterSelected.filterName ? filterSelected.filterName : 'FILTER'}
                          </MobileLabel>
                        </Col>
                        <Col>
                          <Row>
                            <ColIcon>
                              <Icon
                                name="edit"
                                onClick={() => {
                                  controlFilter('edit')
                                }}
                                disabled={true}
                              />
                            </ColIcon>
                            <ColIcon>
                              <Icon
                                name="trash"
                                onClick={() => {
                                  formikProps.setValues({
                                    filter: {
                                      id: '',
                                      name: '',
                                      referenceNumber: '',
                                      rootOperationReferenceNumber: '',
                                      claimUcr: '',
                                      claimEcfTransaction: '',
                                      client: '',
                                      mainParty: '',
                                      status: '',
                                    }
                                  })
                                  controlFilter('delete')
                                }}
                              />
                            </ColIcon>
                            <ColIcon>
                              <Icon
                                name="add-grey"
                                onClick={() => {
                                  formikProps.setValues({
                                    filter: {
                                      id: '',
                                      name: '',
                                      referenceNumber: '',
                                      rootOperationReferenceNumber: '',
                                      claimUcr: '',
                                      claimEcfTransaction: '',
                                      client: '',
                                      mainParty: '',
                                      status: '',
                                    }
                                  })
                                  controlFilter('create')
                                }}
                              />
                            </ColIcon>
                          </Row>

                        </Col>
                      </Row>
                      <RowFilter>
                        {isAbleEdit || isAbleCreate ?
                          <FilterColumn>
                            <Field
                              name="filter.name"
                              placeholder="Filter Name"
                              value={formikProps.values.filter.name}
                              component={InputField}
                              type="text"
                              onChange={(e) => {
                                formikProps?.setFieldValue("name", e);
                              }}
                              validation={false}
                              list={true}
                            />
                          </FilterColumn>
                          :
                          <FilterColumn>
                            <Field
                              name="filtersList"
                              label="My filters"
                              isRequired
                              options={filterOptions}
                              onChange={(e) => {
                                setFilterSelected(filtersList[e])
                                formikProps.setValues({
                                  filter: {
                                    id: filtersList[e].id,
                                    name: filtersList[e].filterName,
                                    referenceNumber: filtersList[e].filterArgs.referenceNumber,
                                    rootOperationReferenceNumber: filtersList[e].filterArgs.rootOperationReferenceNumber,
                                    claimUcr: filtersList[e].filterArgs.claimUcr,
                                    claimEcfTransaction: filtersList[e].filterArgs.claimEcfTransaction,
                                    client: filtersList[e].filterArgs.client,
                                    mainParty: filtersList[e].filterArgs.mainParty,
                                    status: filtersList[e].filterArgs.status,
                                  }
                                })
                                formikProps?.setFieldValue("filtersList", filterOptions[e].value);
                              }}
                              component={SelectField}
                            />
                          </FilterColumn>
                        }
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={
                                formikProps.values.filter
                                  .rootOperationReferenceNumber.length
                              }
                              onChange={() =>
                                formik.current?.setFieldValue(
                                  "filter.rootOperationReferenceNumber",
                                  ""
                                )
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.rootOperationReferenceNumber"
                            placeholder="Reference Number"
                            component={InputField}
                            validation={false}
                            onPressEnter={() => handleFilter()}
                          />
                        </FilterColumn>
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={
                                formikProps.values.filter.referenceNumber.length
                              }
                              onChange={() =>
                                formik.current?.setFieldValue(
                                  "filter.referenceNumber",
                                  ""
                                )
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.referenceNumber"
                            placeholder="Claim Id"
                            component={InputField}
                            validation={false}
                            onPressEnter={() => handleFilter()}
                          />
                        </FilterColumn>
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={formikProps.values.filter.client.length}
                              onChange={() =>
                                formik.current?.setFieldValue("filter.client", "")
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.client"
                            placeholder="Client"
                            component={InputField}
                            validation={false}
                            onPressEnter={() => handleFilter()}
                          />
                        </FilterColumn>
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={formikProps.values.filter.mainParty.length}
                              onChange={() =>
                                formik.current?.setFieldValue(
                                  "filter.mainParty",
                                  ""
                                )
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.mainParty"
                            placeholder="Main Party"
                            component={InputField}
                            validation={false}
                            onPressEnter={() => handleFilter()}
                          />
                        </FilterColumn>
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={formikProps.values.filter.products?.length}
                              onChange={() =>
                                formik.current?.setFieldValue("filter.products", "")
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.products"
                            placeholder="Products"
                            component={MultiSelectField}
                            validation={false}
                            options={products}
                            value={formikProps.values.filter.products}
                            filter={true}
                            corDiferente={false}
                            format={(value) => {
                              const selectedProducts = products.filter(option => value.includes(option.value))
                              return selectedProducts.map(item => item.label).join(', ')
                            }}
                            onChange={(e) => {
                              if (!e.length) {
                                formik.current?.setFieldValue(
                                  "filter.products",
                                  ""
                                )
                              } else {
                                formik.current?.setFieldValue(
                                  "filter.products",
                                  e
                                )
                              }
                            }}
                          />
                        </FilterColumn>
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={formikProps.values.filter.claimUcr.length}
                              onChange={() =>
                                formik.current?.setFieldValue(
                                  "filter.claimUcr",
                                  ""
                                )
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.claimUcr"
                            placeholder="UCR"
                            component={InputField}
                            validation={false}
                            onPressEnter={() => handleFilter()}
                          />
                        </FilterColumn>
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={
                                formikProps.values.filter.claimEcfTransaction
                                  .length
                              }
                              onChange={() =>
                                formik.current?.setFieldValue(
                                  "filter.claimEcfTransaction",
                                  ""
                                )
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.claimEcfTransaction"
                            placeholder="ECF"
                            component={InputField}
                            validation={false}
                            onPressEnter={() => handleFilter()}
                          />
                        </FilterColumn>
                      </RowFilter>

                      <RowFilter>
                        <CheckBoxColumn>
                          <Label>
                            <Checkbox
                              checked={formikProps.values.filter.status.length}
                              onChange={() =>
                                formik.current?.setFieldValue("filter.status", "")
                              }
                            />
                          </Label>
                        </CheckBoxColumn>
                        <FilterColumn>
                          <FieldGrid
                            name="filter.status"
                            placeholder="Status"
                            component={MultiSelectField}
                            validation={false}
                            options={headerStatus}
                            value={formikProps.values.filter.status}
                            filter={true}
                            corDiferente={false}
                            format={(value) => {
                              const selectedStatus = headerStatus.filter(option => value.includes(option.value))
                              return selectedStatus.map(item => item.label).join(', ')
                            }}
                            onChange={(e) => {
                              if (!e.length) {
                                formik.current?.setFieldValue(
                                  "filter.status",
                                  ""
                                )
                              } else {
                                formik.current?.setFieldValue(
                                  "filter.status",
                                  e
                                )
                              }
                            }}
                          />
                        </FilterColumn>
                      </RowFilter>
                    </FilterSearchContainer>

                    <Row align="center" className="apply-button-container">
                      <Col>
                        <Button
                          primary
                          type="button"
                          id="btnFilter"
                          style={{ textAlign: "center" }}
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            handleCustomFilter(formikProps.values)
                          }}
                        >
                          {isAbleEdit || isAbleCreate ? 'Save' : 'Apply'}
                        </Button>
                      </Col>
                    </Row>
                  </FilterContainer>

                  <div style={{ display: 'flex', justifyContent: 'space-between', width: '100%' }}>
                    <FilterLabel>
                      {filterSelected.filterName ? `FILTER: ${filterSelected.filterName}` : ''}
                    </FilterLabel>
                    <div style={{ display: 'flex', alignItems: 'center' }}>

                      <ColIcon>
                        <Icon
                          name="exportExcel"
                          title="Export Excel"
                          onClick={() => handleExportClaims(1, sortBy, location.search || undefined)}
                        />
                      </ColIcon>
                      <TooltipCard>
                        <TooltipText>
                          <Icon
                            name="filter"
                            onClick={(e) => setShowFilter(!showFilter)}
                          />
                        </TooltipText>
                        <TooltipBox>
                          {tooltips[585]}
                        </TooltipBox>
                      </TooltipCard>
                      <TooltipCard>
                        <TooltipText>
                          <ColIcon>
                            <Icon name="filterCan" onClick={() => resetFilters()} />
                          </ColIcon>
                        </TooltipText>
                        <TooltipBox>
                          {tooltips[586]}
                        </TooltipBox>
                      </TooltipCard>
                    </div>
                  </div>
                </RowFilterIcon>


                <ResponsiveTable formikRef={formik?.current} columns={columns} data={claims} />

                {!claims.length && < ButtonContainer style={{ minHeight: showFilter ? '450px' : '' }}>
                  <Text style={{ fontWeight: 'bold', color: Colors.GRAY[1], margin: 'auto', cursor: 'pointer', marginTop: '10px' }} onClick={() => { }}>No Items to display</Text>
                </ButtonContainer>}
              </Form>
            )
          }}
        </Formik>
        <LoadContainer>
          {!claims || claims.length === 0 || currentPage === totalPages ? (
            <Button forth onClick={(e) => e.preventDefault()}>
              NO MORE ITEMS
            </Button>
          ) : (
            <Button forth onClick={() => setNextPage()}>
              +LOAD MORE ACCOUNTS
            </Button>
          )}
        </LoadContainer>
      </Content>
      {loading.active && <Loader backdropColor={Colors.WHITE} fullScreen />}
    </Layout>
  );
}
